import React, { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  Avatar,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Backdrop,
  CircularProgress
} from '@mui/material';
import { CloudUpload, Image, Delete, Print, Save, MoveToInbox } from '@mui/icons-material';
import Header from '../Header';
import axios from 'axios';
import { endpoint } from '../../resource/Constant';
import { useNavigate } from 'react-router-dom';
import PrescriptionLoader from './PrescriptionLoader';
import { useEffect } from 'react';
import { SmartToy } from '@mui/icons-material'; // For AI processing icon
import { motion } from 'framer-motion';

export default function UploadPrescription() {
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isAnalyzed, setIsAnalyzed] = useState(false);
  const [analyzedData, setAnalyzedData] = useState(null);
  const formData = new FormData(); // store API result
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const navigate = useNavigate();
  const [analyzing, setAnalyzing] = useState(false);
  const [aiProcessing, setAiProcessing] = useState(false);
  const [dots, setDots] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [loader, setLoader] = useState(false);

  const handleClose = () => {
    setOpenDialog(false);
  };

  const handleOpen = () => {
    setOpenDialog(true);
  };

  const stepVariants = {
    initial: { opacity: 0, x: 50 }, // Start off-screen
    animate: { opacity: 1, x: 0 }, // Slide in
    exit: { opacity: 0, x: -50 } // Slide out
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setPreviewUrl(URL.createObjectURL(selectedFile));
    }
  };

  const handleRemoveFile = () => {
    setFile(null);
    setPreviewUrl(null);
  };

  const sendEmail = () => {
    setLoader(true);
    axios
      .post(endpoint + '/api/prescription/send-email', null, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${localStorage.getItem('presdecttoken')}`
        }
      })
      .then((res) => {
        setSnackbar({
          open: true,
          message: res?.data?.data?.message || 'Email sent successfully!',
          severity: 'success'
        });
        handleClose();
        setLoader(false);
      })
      .catch((error) => {
        setSnackbar({
          open: true,
          message: error?.response?.data?.message || 'An error occurred while sending the email.',
          severity: 'error'
        });
        handleClose();
        setLoader(false);
      });
  };

  const handleAnalyze = async () => {
    if (!file) {
      alert('Please upload a file first!');
      return;
    }
    setAnalyzing(true);

    formData.append('file', file);

    axios
      .post(endpoint + '/api/prescription/generate-prescription-updated', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${localStorage.getItem('presdecttoken')}`
        }
      })
      .then((res) => {
        const detectedPrescription = res?.data?.data?.response;
        setAnalyzedData(detectedPrescription);
        setIsAnalyzed(true);
        setAnalyzing(false);
      })
      .catch((error) => {
        setAnalyzing(false);

        if (error?.status === 403) {
          handleOpen();
          return;
        }

        setSnackbar({
          open: true,
          message: error?.response?.data?.message || 'An error occurred while analyzing the prescription.',
          severity: 'error'
        });

        if (error?.status === 401 || error?.status === 402) {
          localStorage.removeItem('presdecttoken');
          setTimeout(() => {
            navigate('/prescription/login');
          }, 1000);
        }
      });
  };
  const handleAnalyzeAnother = () => {
    setFile(null);
    setPreviewUrl(null);
    setIsAnalyzed(false);
    setAnalyzedData(null);
  };

  useEffect(() => {
    if (!aiProcessing) return;

    const interval = setInterval(() => {
      setDots((prev) => (prev.length >= 3 ? '' : prev + '.'));
    }, 500);

    return () => clearInterval(interval);
  }, [aiProcessing]);

  const getBackgroundColor = (severity) => {
    switch (severity) {
      case 'success':
        return '#2e7d32';
      case 'error':
        return '#d32f2f';
      case 'warning':
        return '#ed6c02';
      case 'info':
      default:
        return '#0D47A1';
    }
  };

  return (
    <>
      <Header />
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: 'flex-start',
          justifyContent: 'center',
          bgcolor: '#f9fafb',
          p: 4,
          gap: 4,
          marginTop: 13
        }}
      >
        {!isAnalyzed ? (
          // Upload Section
          <Paper
            elevation={3}
            sx={{
              p: 4,
              width: { xs: '100%', md: '50%' },
              borderRadius: 3,
              textAlign: 'center'
            }}
          >
            <Box
              sx={{
                border: '2px dashed #ccc',
                borderRadius: 2,
                p: 6,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 2,
                position: 'relative'
              }}
            >
              {!previewUrl ? (
                <>
                  <IconButton sx={{ bgcolor: 'primary.light', p: 2 }}>
                    <Image sx={{ fontSize: 40, color: 'primary.main' }} />
                  </IconButton>
                  <Typography variant="h6" fontWeight="bold">
                    Upload Prescription
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={2}>
                    Drag and drop your prescription image or PDF here, or click to browse
                  </Typography>

                  <input accept=".jpg,.png,.pdf" type="file" id="file-upload" hidden onChange={handleFileChange} />
                  <label htmlFor="file-upload">
                    <Button variant="contained" component="span" startIcon={<CloudUpload />}>
                      Browse Files
                    </Button>
                  </label>
                </>
              ) : (
                <>
                  <Box
                    component="img"
                    src={previewUrl}
                    alt="Preview"
                    sx={{
                      maxHeight: 200,
                      objectFit: 'contain',
                      borderRadius: 2,
                      border: '1px solid #ddd',
                      mb: 2
                    }}
                  />
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    {file.name} ({(file.size / 1024).toFixed(1)} KB)
                  </Typography>
                  <Button variant="outlined" color="error" size="small" startIcon={<Delete />} onClick={handleRemoveFile}>
                    Remove
                  </Button>
                </>
              )}
            </Box>
            <Box>
              <Button variant="contained" fullWidth sx={{ mt: 4 }} onClick={handleAnalyze} disabled={!file || analyzing}>
                {analyzing ? 'Analyzing...' : 'Analyze Prescription'}
              </Button>

              {/* Use the Material UI loader with proper props */}
              <PrescriptionLoader open={analyzing} onClose={() => setAnalyzing(false)} />
            </Box>
          </Paper>
        ) : (
          // Result Section
          <Paper
            elevation={3}
            sx={{
              p: 4,
              width: { xs: '100%', md: '60%' },
              borderRadius: 3
            }}
          >
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
              <Typography variant="h5" fontWeight="bold" color="primary.main">
                Prescription Analysis Results
              </Typography>
            </Box>

            <Box mb={2} display={'flex'} flexDirection="row" gap={0.5}>
              <Typography variant="h8" fontWeight="bold">
                Patient:
              </Typography>
              <Typography marginRight="10px" minWidth="100px" color="black" mb={1}>
                {analyzedData?.patientName}
              </Typography>

              <Typography variant="h8" fontWeight="bold">
                Prescriber:
              </Typography>
              <Typography marginRight="10px" color="black" mb={1}>
                {analyzedData?.prescriberName}
              </Typography>

              <Typography variant="h8" fontWeight="bold">
                Prescription Date:
              </Typography>
              <Typography marginRight="10px" color="black" mb={1}>
                {analyzedData?.prescriptionDate}
              </Typography>
            </Box>

            <Typography variant="h6" fontWeight="bold">
              Medications
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <strong>Name</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Description</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Instructions</strong>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {analyzedData?.medications?.map((med, index) => (
                    <TableRow key={index}>
                      <TableCell>{med.name}</TableCell>
                      <TableCell>{med.description}</TableCell>
                      <TableCell>{med.instructions}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Typography variant="h8" fontWeight="bold">
              NOTE: This information is AI-generated and for general guidance only. Always consult a licensed healthcare provider before
              starting or changing any medication.
            </Typography>
            <Box display="flex" justifyContent="flex-end">
              <Button variant="contained" onClick={handleAnalyzeAnother}>
                Analyze Another Prescription
              </Button>
            </Box>
          </Paper>
        )}

        {/* How to Use Section */}
        <Paper
          elevation={3}
          sx={{
            p: 4,
            width: { xs: '100%', md: '30%' },
            borderRadius: 3
          }}
        >
          <Typography variant="h5" fontWeight="bold" color="primary.main" mb={3}>
            How to Use
          </Typography>

          <List sx={{ mb: 3 }}>
            {[
              'Upload your prescription by dragging & dropping the file or using the upload button',
              'Click the Analyze button to process your prescription',
              'Review the extracted information for accuracy'
            ].map((text, index) => (
              <ListItem key={index} alignItems="flex-start">
                <ListItemAvatar>
                  <Avatar sx={{ bgcolor: 'primary.light' }}>{index + 1}</Avatar>
                </ListItemAvatar>
                <ListItemText primary={text} />
              </ListItem>
            ))}
          </List>

          <Paper
            elevation={0}
            sx={{
              bgcolor: 'blue.50',
              p: 2,
              borderRadius: 2,
              textAlign: 'center',
              color: 'primary.main',
              fontWeight: 600
            }}
          >
            Supported formats: JPG, PNG, PDF
          </Paper>
        </Paper>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={aiProcessing ? null : 4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        TransitionProps={{ direction: 'down' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{
            width: '100%',
            backgroundColor: getBackgroundColor(snackbar.severity),
            color: '#fff',
            fontWeight: 'bold',
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            borderRadius: '8px',
            '& .MuiAlert-icon': {
              color: '#fff',
              fontSize: '24px',
              marginRight: '12px'
            }
          }}
        >
          <div className="flex items-center gap-2">
            {snackbar.message}
            {aiProcessing && (
              <div className="flex items-center ml-1">
                <SmartToy
                  sx={{
                    fontSize: '18px',
                    marginRight: '4px',
                    animation: 'pulse 1.5s infinite',
                    '@keyframes pulse': {
                      '0%, 100%': { opacity: 0.6 },
                      '50%': { opacity: 1 }
                    }
                  }}
                />
                <span style={{ fontStyle: 'italic', fontSize: '0.9rem' }}>AI processing{dots}</span>
              </div>
            )}
          </div>
        </Alert>
      </Snackbar>

      <Dialog
        open={openDialog}
        keepMounted
        onClose={handleClose}
        aria-describedby="alert-dialog-slide-description"
        PaperProps={{
          sx: { minHeight: 250, padding: 2 } // Increase height and add padding
        }}
      >
        <DialogTitle sx={{ fontWeight: 'bold' }}>⚠️ You have exceeded the maximum allowed attempts</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-slide-description">
            🚫 You’ve reached the maximum number of attempts for prescription analysis. <br />
            <br />
            If you’d like to continue using this tool or integrate it into your application, please contact us by clicking the ✉️{' '}
            <strong>Send Email</strong> button below.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Close</Button>
          <Button variant="contained" color="primary" onClick={sendEmail}>
            ✉️ Send Email
          </Button>
        </DialogActions>
      </Dialog>

      <Backdrop
        open={loader}
        sx={{
          zIndex: (theme) => theme.zIndex.modal + 1,
          color: '#fff'
        }}
      >
        <svg width={0} height={0}>
          <defs>
            <linearGradient id="my_gradient" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#4169E1" />
              <stop offset="30%" stopColor="#f5f5f5" />
              <stop offset="100%" stopColor="#fff" />
            </linearGradient>
          </defs>
        </svg>
        <CircularProgress sx={{ 'svg circle': { stroke: 'url(#my_gradient)' } }} size={70} thickness={3} />
      </Backdrop>
    </>
  );
}
